import type { LatLng } from '@mobi/shared';

export interface MapMarker {
  point: LatLng;
  kind: 'origin' | 'destination' | 'driver' | 'passenger';
  label?: string;
}

export interface MapBounds {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

export interface RideMapProps {
  markers: MapMarker[];
  /** janela fixa; sem ela, enquadra os marcadores */
  bounds?: MapBounds;
  /** polyline codificada (precisão 5) devolvida pelo roteador */
  routePolyline?: string | null;
  /** habilita escolher um ponto tocando no mapa */
  onPick?: (point: LatLng) => void;
  className?: string;
  interactive?: boolean;
}

/** Janela geográfica quadrada em torno de um ponto, com raio em metros. */
export function boundsAround(center: LatLng, radiusMeters: number): MapBounds {
  const dLat = radiusMeters / 111_320;
  const dLng = radiusMeters / (111_320 * Math.max(0.2, Math.cos((center.lat * Math.PI) / 180)));
  return {
    minLat: center.lat - dLat,
    maxLat: center.lat + dLat,
    minLng: center.lng - dLng,
    maxLng: center.lng + dLng,
  };
}

/**
 * Decodifica polyline com precisão 5 — o formato que o OpenRouteService
 * devolve em `geometry`.
 */
export function decodePolyline(encoded: string): LatLng[] {
  const points: LatLng[] = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    for (const axis of ['lat', 'lng'] as const) {
      let result = 0;
      let shift = 0;
      let byte: number;
      do {
        byte = encoded.charCodeAt(index++) - 63;
        result |= (byte & 0x1f) << shift;
        shift += 5;
      } while (byte >= 0x20);

      const delta = result & 1 ? ~(result >> 1) : result >> 1;
      if (axis === 'lat') lat += delta;
      else lng += delta;
    }
    points.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }

  return points;
}

const LEGEND_COLOR: Record<MapMarker['kind'], string> = {
  origin: 'bg-ink',
  destination: 'bg-brand',
  driver: 'bg-good',
  passenger: 'bg-warn',
};

export function MapLegend({ items }: { items: { kind: MapMarker['kind']; label: string }[] }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-faint">
      {items.map((i) => (
        <span key={i.kind} className="inline-flex items-center gap-1.5">
          <span className={`inline-block h-2 w-2 rounded-full ${LEGEND_COLOR[i.kind]}`} />
          {i.label}
        </span>
      ))}
    </div>
  );
}
